import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { ArrowLeft, MessageSquare, Users, BarChart3, Clock, ChevronRight, Bot, User, RefreshCw, AlertCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

export default function AdminChatPage() {
  const { user, isAuthenticated } = useAuth();
  const [stats, setStats] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchData = async () => {
    setLoading(true);
    setError('');
    try {
      const [statsRes, sessionsRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/chat-stats`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/chat-sessions`, { withCredentials: true })
      ]);
      setStats(statsRes.data);
      setSessions(sessionsRes.data.sessions);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al cargar los datos');
    } finally {
      setLoading(false);
    }
  };

  const fetchSessionDetail = async (sessionId) => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/chat-sessions/${sessionId}`, {
        withCredentials: true
      });
      setSelectedSession(response.data);
    } catch (err) {
      setError('Error al cargar la sesión');
    }
  };

  useEffect(() => {
    if (isAuthenticated && user?.role === 'admin') {
      fetchData();
    }
  }, [isAuthenticated, user]);

  if (!isAuthenticated || user?.role !== 'admin') {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="bg-white border border-slate-200 p-8 max-w-md text-center">
          <AlertCircle className="mx-auto text-red-500 mb-4" size={48} />
          <h1 className="text-xl font-bold text-slate-900 mb-2">Acceso denegado</h1>
          <p className="text-slate-600 mb-6">Necesitas permisos de administrador para acceder a esta página.</p>
          <Link to="/" className="text-rose-600 hover:underline">Volver al inicio</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-chat-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin" className="flex items-center gap-2 text-slate-600 hover:text-slate-900">
              <ArrowLeft size={18} />
              <span className="text-sm">Dashboard</span>
            </Link>
            <div className="h-6 w-px bg-slate-200" />
            <h1 className="font-bold text-slate-900">Panel de Administración - Chat</h1>
          </div>
          <button
            onClick={fetchData}
            className="flex items-center gap-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 transition text-sm"
          >
            <RefreshCw size={16} />
            Actualizar
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {error && (
          <div className="bg-red-50 border border-red-200 p-4 mb-6 flex items-center gap-3">
            <AlertCircle className="text-red-500" size={20} />
            <p className="text-red-700">{error}</p>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="spinner" />
          </div>
        ) : (
          <>
            {/* Stats */}
            {stats && (
              <div className="grid md:grid-cols-3 gap-6 mb-8">
                <div className="bg-white border border-slate-200 p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                      <MessageSquare className="text-blue-600" size={24} />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Total sesiones</p>
                      <p className="text-3xl font-bold text-slate-900">{stats.total_sessions}</p>
                    </div>
                  </div>
                </div>
                <div className="bg-white border border-slate-200 p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                      <Users className="text-green-600" size={24} />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Sesiones hoy</p>
                      <p className="text-3xl font-bold text-slate-900">{stats.sessions_today}</p>
                    </div>
                  </div>
                </div>
                <div className="bg-white border border-slate-200 p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-rose-100 rounded-full flex items-center justify-center">
                      <BarChart3 className="text-rose-600" size={24} />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Total mensajes</p>
                      <p className="text-3xl font-bold text-slate-900">{stats.total_messages}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="grid lg:grid-cols-2 gap-8">
              {/* Sessions list */}
              <div className="bg-white border border-slate-200">
                <div className="p-4 border-b border-slate-200">
                  <h2 className="font-bold text-slate-900">Sesiones de chat</h2>
                  <p className="text-sm text-slate-500">{sessions.length} sesiones registradas</p>
                </div>
                <div className="max-h-[600px] overflow-y-auto">
                  {sessions.length === 0 ? (
                    <div className="p-8 text-center text-slate-500">
                      No hay sesiones de chat todavía
                    </div>
                  ) : (
                    sessions.map((session) => (
                      <button
                        key={session.session_id}
                        onClick={() => fetchSessionDetail(session.session_id)}
                        className={`w-full p-4 border-b border-slate-100 hover:bg-slate-50 transition text-left ${
                          selectedSession?.session_id === session.session_id ? 'bg-rose-50' : ''
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center">
                              <MessageSquare size={18} className="text-slate-500" />
                            </div>
                            <div>
                              <p className="font-medium text-slate-900 text-sm">
                                Sesión #{session.session_id.slice(0, 8)}
                              </p>
                              <p className="text-xs text-slate-500">
                                {session.messages_count} mensajes · IP: {session.ip_address}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-xs text-slate-400">
                              {new Date(session.created_at).toLocaleDateString('es-ES')}
                            </p>
                            <ChevronRight size={16} className="text-slate-300 ml-auto" />
                          </div>
                        </div>
                      </button>
                    ))
                  )}
                </div>
              </div>

              {/* Session detail */}
              <div className="bg-white border border-slate-200">
                <div className="p-4 border-b border-slate-200">
                  <h2 className="font-bold text-slate-900">Detalle de conversación</h2>
                </div>
                {selectedSession ? (
                  <div className="p-4 max-h-[600px] overflow-y-auto">
                    <div className="bg-slate-50 p-4 mb-4 text-xs text-slate-600 space-y-1">
                      <p><strong>ID:</strong> {selectedSession.session_id}</p>
                      <p><strong>IP:</strong> {selectedSession.ip_address}</p>
                      <p><strong>Inicio:</strong> {new Date(selectedSession.created_at).toLocaleString('es-ES')}</p>
                      <p><strong>Última actividad:</strong> {new Date(selectedSession.last_activity).toLocaleString('es-ES')}</p>
                    </div>
                    <div className="space-y-4">
                      {selectedSession.messages.map((msg, index) => (
                        <div
                          key={`${msg.role}-${msg.timestamp || index}`}
                          className={`flex items-start gap-3 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}
                        >
                          <div
                            className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                              msg.role === 'user' ? 'bg-slate-200' : 'bg-rose-100'
                            }`}
                          >
                            {msg.role === 'user' ? (
                              <User size={14} className="text-slate-600" />
                            ) : (
                              <Bot size={14} className="text-rose-600" />
                            )}
                          </div>
                          <div
                            className={`max-w-[80%] p-3 text-sm ${
                              msg.role === 'user'
                                ? 'bg-slate-900 text-white rounded-t-xl rounded-bl-xl'
                                : 'bg-slate-100 text-slate-800 rounded-t-xl rounded-br-xl'
                            }`}
                          >
                            <p className="whitespace-pre-wrap">{msg.content}</p>
                            {msg.timestamp && (
                              <p className={`text-[10px] mt-2 ${msg.role === 'user' ? 'text-slate-400' : 'text-slate-400'}`}>
                                {new Date(msg.timestamp).toLocaleTimeString('es-ES')}
                              </p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="p-8 text-center text-slate-500">
                    <MessageSquare className="mx-auto mb-3 text-slate-300" size={40} />
                    <p>Selecciona una sesión para ver los mensajes</p>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
